import { useEffect, useState } from 'react';
import { featureAdoptionLib } from 'shared/libs/feature-adoption';
import { FeatureAdoptionTypes } from '../domain';
import { orderDetailAdoptionService } from '../services';
import { useOrderDetailAdoption } from './useOrderDetailAdoption';

import type { OrderDetailStateRedux } from 'shared/infra/redux';
import type { AdoptionStep } from 'shared/libs/feature-adoption';
import type { AdoptionData } from '../domain';

// TODO: Remove this functionality after 3 months, launch date 24/Sep/24.
/* Removal date: 24/Dec/24.
  This is part of a feature adoption experiment and should be revisited
  to determine if it should be permanently integrated or deprecated.
  Related items: RA-4020, RA-4023, RA-4251
 */
export function useOrderDetailDialog(
  isOpenDialog: boolean,
  currentOrder: OrderDetailStateRedux,
) {
  const [steps, setSteps] = useState<AdoptionStep[]>([]);

  const { destroyAdoption, startAdoption, tooltip } = featureAdoptionLib();
  const {
    getStoredData,
    updateStoredData,
    hasDayElapsed,
    addInitialStoredData,
    addStoredData,
    hasHandoff,
    isPrintUnset,
    MAX_VIEWS,
    partnerId,
    storeId,
  } = orderDetailAdoptionService(currentOrder);
  const { step } = useOrderDetailAdoption({
    partnerId,
    storeId,
    tooltip,
  });

  useEffect(() => {
    if (!isOpenDialog) {
      setSteps([]);
      return;
    }
    const data = getStoredData();
    if (data.length > 0) {
      updateStoredData(data);
      _prepare(data);
    } else {
      addInitialStoredData();
      _setup();
    }
  }, [isOpenDialog]);

  useEffect(() => {
    if (isOpenDialog && steps.length > 0) {
      startAdoption(steps);
    }
    return () => {
      destroyAdoption();
    };
  }, [isOpenDialog, steps]);

  const _setup = () => {
    const steps: AdoptionStep[] = [
      ...(isPrintUnset ? [step.printed] : []),
      ...(hasHandoff ? [step.handoff] : []),
    ];
    setSteps(steps);
  };

  const _prepare = (data: AdoptionData[]) => {
    let items: AdoptionStep[] = [];
    const hasPrintedAdoptionInfo = data.some(
      val => val.type === FeatureAdoptionTypes.ScalePrinted,
    );
    const hasHandoffInfo = data.some(
      val => val.type === FeatureAdoptionTypes.handoff,
    );

    if (!hasPrintedAdoptionInfo && isPrintUnset) {
      items = [...items, step.printed];
      addStoredData(FeatureAdoptionTypes.ScalePrinted);
    }

    if (!hasHandoffInfo && hasHandoff) {
      items = [...items, step.handoff];
      addStoredData(FeatureAdoptionTypes.handoff);
    }

    items = [
      ...items,
      ...data
        .filter(
          value =>
            value.views < MAX_VIEWS && hasDayElapsed(value?.lastShown || ''),
        )
        .map(value => _prepareStep(value.type))
        .filter((item): item is AdoptionStep => item !== undefined),
    ];

    setSteps(items);
  };

  const _prepareStep = (type: FeatureAdoptionTypes) => {
    switch (type) {
      case FeatureAdoptionTypes.ScalePrinted:
        if (isPrintUnset) return step.printed;
        break;
      case FeatureAdoptionTypes.handoff:
        if (hasHandoff) return step.handoff;
        break;
      default:
        return null;
    }
  };
}
